// <s-marquee> — seamless continuous scroll of its [data-marquee-track] content.
// Repeats the track content until it overfills the container, then doubles it for a gapless loop,
// and animates with the Web Animations API. Rebuilds on resize. Slows down under
// prefers-reduced-motion and pauses on hover. One component per file.
class SMarquee extends HTMLElement {
  #animation = null;
  #ac = null;
  #mql = null;
  #originals = null;
  #resizeObserver = null;
  #lastWidth = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    this.#mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.#mql.addEventListener("change", () => this.#setup(), { signal: this.#ac.signal });

    this.addEventListener("mouseenter", () => this.#animation?.pause(), { signal: this.#ac.signal });
    this.addEventListener("mouseleave", () => this.#animation?.play(), { signal: this.#ac.signal });

    this.#resizeObserver = new ResizeObserver(() => {
      const width = this.clientWidth;
      if (Math.abs(width - this.#lastWidth) > 1) this.#setup();
    });
    this.#resizeObserver.observe(this);

    if (document.fonts?.ready) document.fonts.ready.then(() => this.isConnected && this.#setup());

    this.#setup();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#resizeObserver?.disconnect();
    this.#animation?.cancel();
  }

  #setup() {
    this.#animation?.cancel();
    this.#animation = null;

    const track = this.querySelector("[data-marquee-track]");
    if (!track) return;

    // Remember the authored content once, and reset to it before every rebuild.
    if (!this.#originals) this.#originals = [...track.children];
    track.replaceChildren(...this.#originals);
    this.#lastWidth = this.clientWidth;

    if (this.#originals.length === 0) return;

    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const setWidth = track.scrollWidth + gap;
    if (setWidth <= gap) return;

    // One "half" must be at least as wide as the container so the strip never shows a blank end.
    const copiesPerHalf = Math.max(1, Math.ceil(this.#lastWidth / setWidth));
    const cloneSet = () =>
      this.#originals.map((node) => {
        const clone = node.cloneNode(true);
        clone.setAttribute("aria-hidden", "true");
        return clone;
      });

    for (let i = 1; i < copiesPerHalf * 2; i++) track.append(...cloneSet());

    const distance = setWidth * copiesPerHalf;
    // Always slide; visitors who prefer reduced motion get a gentler pace. Hover pauses it.
    const baseSpeed = parseFloat(this.dataset.speed) || 40;
    const speed = this.#mql.matches ? baseSpeed / 2 : baseSpeed;
    const durationMs = (distance / speed) * 1000;
    const reverse = this.dataset.direction === "right";
    const from = reverse ? -distance : 0;
    const to = reverse ? 0 : -distance;

    this.#animation = track.animate(
      [{ transform: `translateX(${from}px)` }, { transform: `translateX(${to}px)` }],
      { duration: durationMs, iterations: Infinity, easing: "linear" },
    );
  }
}

if (!customElements.get("s-marquee")) {
  customElements.define("s-marquee", SMarquee);
}
